import DebugObject from '@/etc/debug.object'
import VideoAsset from './assets/video.asset'
import ImageAsset from './assets/image.asset'
import GifAsset from './assets/gif.asset'
import AudioAsset from './assets/audio.asset'
import TextAsset from './assets/text.asset'
import D3Asset from './assets/d3.asset'
import CameraAsset from './assets/camera.asset'
import AdjustmentAsset from './assets/adjustment.asset'

import { TransformEffect } from './effects/transform.effect'
import { PostprocessingEffect } from './effects/postprocessing.effect'
import { CloneEffect } from './effects/clone.effect'
import { BlurEffect } from './effects/blur.effect'
import { LyricsEffect } from './effects/lyrics.effect'

export default class AssetFactory extends DebugObject {
  constructor() {
    super()
    this._assets = {}
    this._effects = {}
    this._camera = null
  }

  asset(uuid) {
    return this._assets[uuid] || null
  }

  removeAsset(uuid) {
    const asset = this._assets[uuid] || null
    if (!asset) return
    try { asset.dispose?.() } catch (e) { /* noop */ }
    delete this._assets[uuid]
  }

  reset(hard = true) {
    const prevAssets = this._assets || {}
    const camera = this._camera ? prevAssets[this._camera] : null

    // Dispose old assets (best-effort). Important for decoded GIF ImageBitmaps.
    try {
      Object.values(prevAssets).forEach((a) => {
        if (!a) return
        if (!hard && camera && a.uuid === camera.uuid) return
        try { a.dispose?.() } catch (e) { /* noop */ }
      })
    } catch (e) { /* noop */ }

    this._assets = {}
    this._camera = null
    if (camera && !hard) {
      this._assets[camera.uuid] = camera
      this._camera = camera.uuid
    }
    this._effects = {}
  }

  defineScene(cuuid = null) {
    if (!this._camera) {
      const camera = new CameraAsset(cuuid)
      camera.loadFromFile()
      this._assets[camera.uuid] = camera
      this._camera = camera.uuid
      this.dbg.log('camera defined')
    }
    return {
      camera: this._assets[this._camera],
    }
  }

  /**
   * Create a non-media Adjustment Layer asset (used by adjustment strips).
   * @param {string|null} uuid
   * @param {Object|null} meta
   */
  createAdjustment(uuid = null, meta = null) {
    const asset = new AdjustmentAsset(uuid)
    asset.loadFromFile()
    if (meta && typeof meta === 'object') {
      asset._asset.meta = { ...(asset._asset.meta || {}), ...(meta || {}) }
    }
    this._assets[asset.uuid] = asset
    return asset
  }

  /**
   * Ensure a non-media asset exists from serialized strip.asset object.
   * Today used for adjustment strips when loading a project.
   * @param {Object} assetObj
   */
  ensureFromObject(assetObj) {
    if (!assetObj || !assetObj.uuid || !assetObj.type) return null
    if (this._assets[assetObj.uuid]) return this._assets[assetObj.uuid]
    if (assetObj.type === 'adjustment') {
      return this.createAdjustment(assetObj.uuid, assetObj.meta || {})
    }
    return null
  }

  fromFile(file, uuid = null) {
    this.dbg.log('fromFile', file, uuid)
    let asset = null
    const type = ((file && file.type) || '').toLowerCase()
    const name = ((file && file.name) || '').toLowerCase()
    const isGif = type === 'image/gif' || name.endsWith('.gif')

    if (type.startsWith('video')) asset = new VideoAsset(uuid)
    else if (isGif) asset = new GifAsset(uuid)
    else if (type.startsWith('image')) asset = new ImageAsset(uuid)
    else if (type.startsWith('audio')) asset = new AudioAsset(uuid)
    else if (name.endsWith('glb')) asset = new D3Asset(uuid)
    if (asset) {
      asset.loadFromFile(file)
      this._assets[asset.uuid] = asset
    }
    return asset
  }

  fromBase64(str64) {
    this.dbg.log('from base64', str64)
  }

  async fromText(text, thumbnail = null, options = {}) {
    const asset = new TextAsset()
    await asset.loadText(text, thumbnail, options)
    this._assets[asset.uuid] = asset
    return asset
  }

  effects(raw = false) {
    const ret = [
      BlurEffect,
      TransformEffect,
      PostprocessingEffect,
      CloneEffect,
      LyricsEffect,
    ]
    if (raw) return ret
    return ret.map((e) => e.metaInfo())
  }

  createEffectPreset(data, merge = {}) {
    this.dbg.log('createEffectPreset', data, merge)
    const Effect = this.effects(true).find(
      (E) => E.metaInfo().uuid === data.group
    )
    if (!Effect) return null
    const effect = new Effect(merge.strip, data.uuid)
    effect.preset = data
    // this._effects[data.uuid] = effect
    // effect.configure({ ...data.preset, ...merge })
    effect.configure({ ...data.preset })
    return {
      ...effect.info(),
      ...merge,
      isPreset: effect.isPreset,
    }
  }

  getEffect(data) {
    this.dbg.log('getEffect', data)
    if (this._effects[data.uuid]) {
      const cached = this._effects[data.uuid]
      // Belt+suspenders: ensure cached instance is bound to the host strip uuid (effects serialize host as `object`)
      try {
        const host = data && (data.object || data.strip)
        if (host && cached._object !== host) cached._object = host
      } catch (e) { /* noop */ }
      return cached
    }
    const Effect = this.effects(true).find(
      (E) => E.metaInfo().uuid === data.code
    )
    if (!Effect) return null
    const host = data && (data.object || data.strip)
    const effect = new Effect(host, data.uuid)
    this._effects[data.uuid] = effect
    effect.configure(data)
    return effect
  }

  createEffect(data, merge = {}) {
    this.dbg.log('factory@createEffect', data)
    const Effect = this.effects(true).find(
      (E) => E.metaInfo().uuid === data.effect
    )
    if (!Effect) return null
    const effect = new Effect(data.strip)
    // effect.time = data.time
    this.dbg.log('addEffect', effect.info())
    // this._effects[effect.uuid] = effect
    const info = {
      ...effect.info(),
      ...merge,
    }
    return info
  }

  addEffectPreset(data) {
    const Effect = this.effects(true).find(
      (E) => E.metaInfo().uuid === data.preset.group
    )
    if (!Effect) return null
    const effect = new Effect(data.strip)
    effect.time = data.time
    this.dbg.log('addEffectPreset', data.preset.group)
    effect.configure(data.preset.preset)
    effect.preset = data.preset
    this._effects[effect.uuid] = effect
    return this._effects[effect.uuid]
  }

  effect(uuid) {
    return this._effects[uuid] || null
  }

  effectMetaInfo(uuid) {
    const Effect = this.effects(true).find((E) => E.metaInfo().uuid === uuid)
    if (!Effect) return null
    return Effect.metaInfo()
  }

  effectInfo(uuid) {
    const e = this.effect(uuid)
    if (!e) return null
    return e.info()
  }

  removeEffect(uuid) {
    if (!this.effect(uuid)) return
    delete this._effects[uuid]
  }
}
